/**
 * Grid Map Viewer — Dev tool page for testing the grid map engine.
 * Generate maps, move around with WASD, toggle fog/visibility, edit walls.
 */

import { useState, useCallback, useEffect } from 'react';
import { Grid3x3, Shuffle, Eye, EyeOff, Grid2x2, Bug, Map as MapIcon, Pen, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GridMapCanvas } from '@/components/game-ui/GridMapCanvas';
import { cn } from '@/lib/utils';
import {
  generateMap,
  createGridMapState,
  applyInput,
  coordKey,
  PRESET_LABELS,
  DEFAULT_RENDER_CONFIG,
  DEFAULT_GENERATOR_CONFIG,
  type GridMapState,
  type GeneratorConfig,
  type GeneratorType,
  type RenderConfig,
  type CardinalDirection,
  type MoveDirection,
  type CityDistrictType,
  type DungeonTheme,
  type CityDistrictConfig,
} from '@omen-quest/grid-map';

const GENERATOR_LABELS: Record<GeneratorType, string> = {
  bsp_dungeon: 'BSP Dungeon',
  cave: 'Cave',
  arena: 'Arena',
  city: 'City',
  preset: 'Preset',
};

const ALL_DISTRICT_TYPES: CityDistrictType[] = ['residential', 'market', 'noble', 'slums'];

const DUNGEON_THEMES: DungeonTheme[] = ['fortress', 'crypt', 'lair', 'ruins', 'sewer'];

const DISTRICT_LABELS: Record<CityDistrictType, string> = {
  residential: 'Residential',
  market: 'Market',
  noble: 'Noble',
  slums: 'Slums',
  temple: 'Temple',
  waterfront: 'Waterfront',
  craftsmen: 'Craftsmen',
};

const THEME_LABELS: Record<DungeonTheme, string> = {
  fortress: 'Fortress',
  crypt: 'Crypt',
  lair: 'Lair',
  ruins: 'Ruins',
  sewer: 'Sewer',
};

export function GridMapViewer() {
  const [genConfig, setGenConfig] = useState<GeneratorConfig>({ ...DEFAULT_GENERATOR_CONFIG });
  const [renderConfig, setRenderConfig] = useState<RenderConfig>({ ...DEFAULT_RENDER_CONFIG });
  const [state, setState] = useState<GridMapState | null>(null);
  const [editMode, setEditMode] = useState<'none' | 'wall'>('none');

  // City-specific state
  const [selectedDistricts, setSelectedDistricts] = useState<Set<CityDistrictType>>(new Set());

  // Dungeon-specific state
  const [dungeonTheme, setDungeonTheme] = useState<DungeonTheme>('fortress');
  const [enableSecrets, setEnableSecrets] = useState(false);
  const [enableKeys, setEnableKeys] = useState(false);

  // ============================================================================
  // GENERATE
  // ============================================================================

  const generate = useCallback(() => {
    const config = { ...genConfig };

    // Apply city district config
    if (config.type === 'city' && selectedDistricts.size > 0) {
      config.districts = [...selectedDistricts].map(type => ({ type } as CityDistrictConfig));
    }

    // Apply dungeon config
    if (config.type === 'bsp_dungeon') {
      config.dungeonTheme = dungeonTheme;
      config.enableSecrets = enableSecrets;
      config.enableKeys = enableKeys;
    }

    const map = generateMap(config);
    const newState = createGridMapState(map);
    setState(newState);
  }, [genConfig, selectedDistricts, dungeonTheme, enableSecrets, enableKeys]);

  // Generate on mount
  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const randomize = useCallback(() => {
    const newSeed = Math.floor(Math.random() * 999999);
    setGenConfig(prev => ({ ...prev, seed: newSeed }));
  }, []);

  // ============================================================================
  // KEYBOARD HANDLER
  // ============================================================================

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (!state) return;
      // Don't capture if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;

      const dirMap: Record<string, 'up' | 'down' | 'left' | 'right'> = {
        w: 'up', W: 'up', ArrowUp: 'up',
        s: 'down', S: 'down', ArrowDown: 'down',
        a: 'left', A: 'left', ArrowLeft: 'left',
        d: 'right', D: 'right', ArrowRight: 'right',
      };

      const dir = dirMap[e.key];
      if (dir) {
        e.preventDefault();
        setState(prev => prev ? applyInput(prev, { type: 'move', direction: dir }) : prev);
        return;
      }

      // Toggle shortcuts
      if (e.key === 'f' || e.key === 'F') {
        setRenderConfig(prev => ({ ...prev, fogEnabled: !prev.fogEnabled }));
      }
      if (e.key === 'g' || e.key === 'G') {
        setRenderConfig(prev => ({ ...prev, showGrid: !prev.showGrid }));
      }
      if (e.key === 'r' || e.key === 'R') {
        generate();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state, generate]);

  // ============================================================================
  // CLICK HANDLERS
  // ============================================================================

  const handleWallClick = useCallback((x: number, y: number, dir: CardinalDirection) => {
    if (editMode !== 'wall' || !state) return;
    setState(prev => prev ? applyInput(prev, { type: 'toggle_wall', x, y, direction: dir }) : prev);
  }, [editMode, state]);

  const handleCellClick = useCallback((x: number, y: number) => {
    if (!state) return;
    const player = state.map.entities.find(e => e.id === state.playerId);
    if (!player) return;
    const dx = x - player.x;
    const dy = y - player.y;

    // Check if clicking an adjacent closed door — open it without moving
    const isAdjacent = Math.abs(dx) + Math.abs(dy) === 1;
    const cell = state.map.cells[y]?.[x];
    if (isAdjacent && cell?.terrain === 'door' && !state.openDoors.has(coordKey(x, y))) {
      setState(prev => prev ? applyInput(prev, { type: 'toggle_door', x, y }) : prev);
      return;
    }

    // Only allow cardinal adjacency (exactly 1 step)
    let dir: MoveDirection | null = null;
    if (dx === 0 && dy === -1) dir = 'up';
    else if (dx === 0 && dy === 1) dir = 'down';
    else if (dx === -1 && dy === 0) dir = 'left';
    else if (dx === 1 && dy === 0) dir = 'right';
    if (dir) {
      setState(prev => prev ? applyInput(prev, { type: 'move', direction: dir! }) : prev);
    }
  }, [state]);

  const handleMoveRequest = useCallback((direction: MoveDirection) => {
    setState(prev => prev ? applyInput(prev, { type: 'move', direction }) : prev);
  }, []);

  const toggleDistrict = useCallback((type: CityDistrictType) => {
    setSelectedDistricts(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  // ============================================================================
  // COMPUTED STATS
  // ============================================================================

  const player = state?.map.entities.find(e => e.id === state.playerId);
  const exploredCount = state?.fogGrid.flat().filter(f => f !== 'hidden').length ?? 0;
  const totalFloor = state?.map.cells.flat().filter(c => c.terrain !== 'wall' && c.terrain !== 'void').length ?? 0;
  const exploredPct = totalFloor > 0 ? Math.round((exploredCount / (state?.map.width ?? 1) / (state?.map.height ?? 1)) * 100) : 0;

  const metadata = state?.map.metadata;

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      {/* Controls bar */}
      <div className="border-b border-white/10 bg-neutral-950 px-4 py-3">
        <div className="flex flex-wrap items-center gap-3 max-w-7xl mx-auto">
          {/* Back button */}
          <Link
            to="/admin"
            className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Admin
          </Link>

          <div className="w-px h-6 bg-white/10" />

          {/* Generator type */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-neutral-500 uppercase tracking-wide">Type</label>
            <select
              className="bg-neutral-900 text-white text-sm border border-white/10 rounded px-2 py-1"
              value={genConfig.type}
              onChange={e => {
                const type = e.target.value as GeneratorType;
                setGenConfig(prev => ({
                  ...prev,
                  type,
                  // City maps need more space for streets + buildings
                  ...(type === 'city' && prev.width < 50 ? { width: 60, height: 45 } : {}),
                }));
              }}
            >
              {Object.entries(GENERATOR_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          {/* Preset selector (only for preset type) */}
          {genConfig.type === 'preset' && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-neutral-500 uppercase tracking-wide">Preset</label>
              <select
                className="bg-neutral-900 text-white text-sm border border-white/10 rounded px-2 py-1"
                value={genConfig.presetName ?? 'pillar_hall'}
                onChange={e => setGenConfig(prev => ({ ...prev, presetName: e.target.value }))}
              >
                {Object.entries(PRESET_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          )}

          {/* City district checkboxes */}
          {genConfig.type === 'city' && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-neutral-500 uppercase tracking-wide">Districts</label>
              {ALL_DISTRICT_TYPES.map(dt => (
                <button
                  key={dt}
                  onClick={() => toggleDistrict(dt)}
                  className={cn(
                    'text-xs rounded px-2 py-0.5 transition-colors border',
                    selectedDistricts.has(dt)
                      ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                      : 'bg-white/5 text-neutral-500 border-white/10 hover:text-neutral-300',
                  )}
                >
                  {DISTRICT_LABELS[dt]}
                </button>
              ))}
              {selectedDistricts.size === 0 && (
                <span className="text-xs text-neutral-600 italic">auto</span>
              )}
            </div>
          )}

          {/* Dungeon theme + toggles */}
          {genConfig.type === 'bsp_dungeon' && (
            <>
              <div className="flex items-center gap-2">
                <label className="text-xs text-neutral-500 uppercase tracking-wide">Theme</label>
                <select
                  className="bg-neutral-900 text-white text-sm border border-white/10 rounded px-2 py-1"
                  value={dungeonTheme}
                  onChange={e => setDungeonTheme(e.target.value as DungeonTheme)}
                >
                  {DUNGEON_THEMES.map(t => (
                    <option key={t} value={t}>{THEME_LABELS[t]}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => setEnableSecrets(prev => !prev)}
                className={cn(
                  'text-xs rounded px-2 py-0.5 transition-colors border',
                  enableSecrets
                    ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                    : 'bg-white/5 text-neutral-500 border-white/10 hover:text-neutral-300',
                )}
              >
                Secrets
              </button>

              <button
                onClick={() => setEnableKeys(prev => !prev)}
                className={cn(
                  'text-xs rounded px-2 py-0.5 transition-colors border',
                  enableKeys
                    ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                    : 'bg-white/5 text-neutral-500 border-white/10 hover:text-neutral-300',
                )}
              >
                Keys
              </button>
            </>
          )}

          {/* Size controls (not for presets) */}
          {genConfig.type !== 'preset' && (
            <>
              <div className="flex items-center gap-1">
                <label className="text-xs text-neutral-500 uppercase tracking-wide">W</label>
                <input
                  type="number"
                  className="bg-neutral-900 text-white text-sm border border-white/10 rounded px-2 py-1 w-16"
                  value={genConfig.width}
                  min={10}
                  max={100}
                  onChange={e => setGenConfig(prev => ({ ...prev, width: Number(e.target.value) }))}
                />
              </div>
              <div className="flex items-center gap-1">
                <label className="text-xs text-neutral-500 uppercase tracking-wide">H</label>
                <input
                  type="number"
                  className="bg-neutral-900 text-white text-sm border border-white/10 rounded px-2 py-1 w-16"
                  value={genConfig.height}
                  min={10}
                  max={100}
                  onChange={e => setGenConfig(prev => ({ ...prev, height: Number(e.target.value) }))}
                />
              </div>
              <div className="flex items-center gap-1">
                <label className="text-xs text-neutral-500 uppercase tracking-wide">Seed</label>
                <input
                  type="number"
                  className="bg-neutral-900 text-white text-sm border border-white/10 rounded px-2 py-1 w-24"
                  value={genConfig.seed}
                  onChange={e => setGenConfig(prev => ({ ...prev, seed: Number(e.target.value) }))}
                />
                <button
                  onClick={randomize}
                  className="p-1 text-neutral-400 hover:text-white transition-colors"
                  title="Randomize seed"
                >
                  <Shuffle className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          {/* Generate button */}
          <button
            onClick={generate}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded px-3 py-1.5 transition-colors"
          >
            <Grid3x3 className="w-4 h-4" />
            Generate
          </button>

          {/* Separator */}
          <div className="w-px h-6 bg-white/10" />

          {/* Toggles */}
          <button
            onClick={() => setRenderConfig(prev => ({ ...prev, fogEnabled: !prev.fogEnabled }))}
            className={cn(
              'flex items-center gap-1.5 text-sm rounded px-2 py-1 transition-colors',
              renderConfig.fogEnabled ? 'bg-blue-500/20 text-blue-400' : 'bg-white/5 text-neutral-500',
            )}
            title="Toggle fog of war (F)"
          >
            {renderConfig.fogEnabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            Fog
          </button>

          <button
            onClick={() => setRenderConfig(prev => ({ ...prev, showGrid: !prev.showGrid }))}
            className={cn(
              'flex items-center gap-1.5 text-sm rounded px-2 py-1 transition-colors',
              renderConfig.showGrid ? 'bg-blue-500/20 text-blue-400' : 'bg-white/5 text-neutral-500',
            )}
            title="Toggle grid lines (G)"
          >
            <Grid2x2 className="w-3.5 h-3.5" />
            Grid
          </button>

          <button
            onClick={() => setRenderConfig(prev => ({ ...prev, showMinimap: !prev.showMinimap }))}
            className={cn(
              'flex items-center gap-1.5 text-sm rounded px-2 py-1 transition-colors',
              renderConfig.showMinimap ? 'bg-blue-500/20 text-blue-400' : 'bg-white/5 text-neutral-500',
            )}
          >
            <MapIcon className="w-3.5 h-3.5" />
            Minimap
          </button>

          <button
            onClick={() => setRenderConfig(prev => ({ ...prev, showDebugVis: !prev.showDebugVis }))}
            className={cn(
              'flex items-center gap-1.5 text-sm rounded px-2 py-1 transition-colors',
              renderConfig.showDebugVis ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 text-neutral-500',
            )}
            title="Toggle debug visibility"
          >
            <Bug className="w-3.5 h-3.5" />
            Debug
          </button>

          <button
            onClick={() => setEditMode(prev => prev === 'wall' ? 'none' : 'wall')}
            className={cn(
              'flex items-center gap-1.5 text-sm rounded px-2 py-1 transition-colors',
              editMode === 'wall' ? 'bg-purple-500/20 text-purple-400' : 'bg-white/5 text-neutral-500',
            )}
            title="Toggle wall editor (click edges to add/remove walls)"
          >
            <Pen className="w-3.5 h-3.5" />
            Walls
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex flex-col-reverse sm:flex-row flex-1 min-h-0">
        {/* Canvas */}
        <div className="flex-1 min-w-0 min-h-0">
          {state ? (
            <GridMapCanvas
              state={state}
              config={renderConfig}
              onCellClick={handleCellClick}
              onWallClick={editMode === 'wall' ? handleWallClick : undefined}
              onMoveRequest={handleMoveRequest}
              className="w-full h-full"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-neutral-600">
              Click Generate to create a map
            </div>
          )}
        </div>

        {/* Sidebar — bottom strip on mobile, side panel on sm+ */}
        <div className="h-32 sm:h-auto sm:w-64 border-t sm:border-t-0 sm:border-l border-white/10 bg-neutral-950 p-4 overflow-x-auto sm:overflow-x-hidden overflow-y-auto">
          <h3 className="text-sm font-medium text-neutral-400 uppercase tracking-wide mb-3">
            Map Info
          </h3>

          {state && (
            <div className="space-y-3 text-sm">
              <InfoRow label="Name" value={state.map.name ?? '—'} />
              <InfoRow label="Size" value={`${state.map.width} x ${state.map.height}`} />
              <InfoRow label="Seed" value={String(state.map.seed ?? '—')} />
              <InfoRow label="Entities" value={String(state.map.entities.length)} />

              <div className="border-t border-white/10 pt-3">
                <h3 className="text-sm font-medium text-neutral-400 uppercase tracking-wide mb-3">
                  Player
                </h3>
                <InfoRow label="Position" value={player ? `(${player.x}, ${player.y})` : '—'} />
                <InfoRow label="Moves" value={String(state.moveCount)} />
                <InfoRow label="Explored" value={`${exploredPct}%`} />
              </div>

              {/* District metadata */}
              {metadata?.districts && metadata.districts.length > 0 && (
                <div className="border-t border-white/10 pt-3">
                  <h3 className="text-sm font-medium text-neutral-400 uppercase tracking-wide mb-3">
                    Districts
                  </h3>
                  <div className="space-y-2">
                    {metadata.districts.map(d => (
                      <div key={d.id} className="flex justify-between">
                        <span className="text-emerald-400 capitalize">{d.type}</span>
                        <span className="text-neutral-500 font-mono text-xs">
                          {d.buildingCount} bldg
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Dungeon zone metadata */}
              {metadata?.dungeonZones && metadata.dungeonZones.length > 0 && (
                <div className="border-t border-white/10 pt-3">
                  <h3 className="text-sm font-medium text-neutral-400 uppercase tracking-wide mb-3">
                    Zones
                  </h3>
                  <div className="space-y-2">
                    {metadata.dungeonZones.map(z => (
                      <div key={z.id} className="flex justify-between">
                        <span className="text-amber-400">{z.theme}</span>
                        <span className="text-neutral-500 font-mono text-xs">
                          {z.roomIds.length} rooms
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Room metadata summary */}
              {metadata?.rooms && metadata.rooms.length > 0 && (
                <div className="border-t border-white/10 pt-3">
                  <h3 className="text-sm font-medium text-neutral-400 uppercase tracking-wide mb-3">
                    Rooms
                  </h3>
                  <div className="space-y-1">
                    {Object.entries(
                      metadata.rooms.reduce<Record<string, number>>((acc, r) => {
                        acc[r.purpose] = (acc[r.purpose] ?? 0) + 1;
                        return acc;
                      }, {})
                    ).map(([purpose, count]) => (
                      <div key={purpose} className="flex justify-between">
                        <span className="text-neutral-400 capitalize">{purpose}</span>
                        <span className="text-neutral-500 font-mono text-xs">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-white/10 pt-3">
                <h3 className="text-sm font-medium text-neutral-400 uppercase tracking-wide mb-3">
                  Controls
                </h3>
                <div className="space-y-1.5 text-neutral-500 text-xs">
                  <p><kbd className="text-neutral-300">WASD</kbd> / <kbd className="text-neutral-300">Arrows</kbd> — Move</p>
                  <p><kbd className="text-neutral-300">Scroll</kbd> — Zoom</p>
                  <p><kbd className="text-neutral-300">Drag</kbd> — Pan</p>
                  <p><kbd className="text-neutral-300">F</kbd> — Toggle fog</p>
                  <p><kbd className="text-neutral-300">G</kbd> — Toggle grid</p>
                  <p><kbd className="text-neutral-300">R</kbd> — Regenerate</p>
                </div>
                <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5 text-neutral-500 text-xs">
                  <p className="text-neutral-400 font-medium">Touch</p>
                  <p>Tap adjacent cell — Move</p>
                  <p>Drag — Pan</p>
                  <p>Pinch — Zoom</p>
                  <p>D-pad — Move</p>
                </div>
              </div>

              {editMode === 'wall' && (
                <div className="border-t border-white/10 pt-3">
                  <div className="text-xs text-purple-400 bg-purple-500/10 rounded p-2">
                    Wall editor active — click near cell edges to toggle walls
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-neutral-500">{label}</span>
      <span className="text-neutral-300 font-mono">{value}</span>
    </div>
  );
}
